/**
 * Connection and synchronization quality.
 *
 * Turns raw measurements into the two things the interface actually shows: a
 * grade for the link, and a grade for how well the two players are matched.
 * Kept here rather than in the UI so that the thresholds are testable and the
 * same everywhere, including in the diagnostics log.
 */

export type QualityGrade = 'excellent' | 'good' | 'fair' | 'poor' | 'lost';

export interface LinkMeasurement {
  /** Best recent round-trip time to the relay. */
  readonly rttMs: number;
  /** Spread of recent clock offsets, which is jitter by another name. */
  readonly jitterMs: number;
  /** Consecutive heartbeats that went unanswered. */
  readonly missedHeartbeats: number;
}

/**
 * Grade the link.
 *
 * A single lost heartbeat is not worth alarming anyone about, so the link is
 * only declared lost after three. Under that, round-trip time dominates: it is
 * what actually bounds how tightly two players can be held together.
 */
export function gradeLink(m: LinkMeasurement): QualityGrade {
  if (m.missedHeartbeats >= 3) return 'lost';
  if (m.missedHeartbeats >= 1) return 'poor';

  // Jitter is weighted more heavily than latency. A steady 200 ms link syncs
  // well; a link swinging between 20 ms and 200 ms does not, because every
  // clock estimate it produces disagrees with the last.
  const score = m.rttMs + m.jitterMs * 3;
  if (score < 60) return 'excellent';
  if (score < 150) return 'good';
  if (score < 400) return 'fair';
  return 'poor';
}

/**
 * Grade how closely the two players are actually matched.
 *
 * The boundaries follow perception rather than round numbers. Below 40 ms is
 * under two frames and genuinely imperceptible. Beyond 400 ms, one person is
 * reacting to things the other has not seen yet, which is the point at which
 * watching together stops working.
 */
export function gradeSync(smoothedErrorMs: number): QualityGrade {
  const e = Math.abs(smoothedErrorMs);
  if (e < 40) return 'excellent';
  if (e < 120) return 'good';
  if (e < 400) return 'fair';
  return 'poor';
}

/** Short human phrase for a grade, used in tooltips and screen reader labels. */
export function describeGrade(grade: QualityGrade): string {
  switch (grade) {
    case 'excellent':
      return 'Excellent';
    case 'good':
      return 'Good';
    case 'fair':
      return 'Fair';
    case 'poor':
      return 'Poor';
    case 'lost':
      return 'Disconnected';
  }
}

/**
 * Format a sync error for display.
 *
 * Sub-millisecond precision would be false confidence, and a flickering
 * decimal is unreadable at a glance, so this rounds to whole milliseconds up
 * to a second and to one decimal beyond it.
 */
export function formatSyncError(errorMs: number): string {
  const e = Math.abs(errorMs);
  if (e < 1) return 'under 1 ms';
  if (e < 1000) return `${Math.round(e)} ms`;
  return `${(e / 1000).toFixed(1)} s`;
}
